import { useEffect, useState } from 'react';

import { usePlatformExecuteAdHocQueryTeam } from '@cloudquery/api-platform';
import { useBaseAuthenticated } from '@cloudquery/base-users';
import { usePagination } from 'shared/base';

import { CardQuery } from '../../../types';

/**
 * The idea for this hook is to have have a pre-fetch of the maximized version of the query.
 * This is to avoid the loading state when the user maximizes the widget.
 */
export const useMaximisableQuery = (query: CardQuery) => {
  const [hasTriggeredMaximizedFetch, setHasTriggeredMaximizedFetch] = useState(false);
  const [maximized, setMaximized] = useState<boolean>();
  const [searchTerm, setSearchTerm] = useState('');
  const { currentMembership } = useBaseAuthenticated();
  const [sortBy, setSortBy] = useState<string | undefined>(
    query.params.sort_by?.length === 1 ? query.params.sort_by?.[0] : undefined,
  );
  const [sortDir, setSortDir] = useState<'asc' | 'desc' | undefined>(
    query.params.sort_dir?.length === 1 ? query.params.sort_dir?.[0] : undefined,
  );

  const defaultPerPage = query.params.per_page ?? 10;
  const { apiPage, perPage, setTablePage, tablePage } = usePagination({
    defaultPerPage,
    tableId: 'table-visualization-card',
  });
  const queryResult = usePlatformExecuteAdHocQueryTeam(
    currentMembership.team.name,
    { query: query.baseQuery },
    {
      ...query.params,
      filter: [
        ...(query.params.filter ?? []),
        ...(searchTerm ? [`search_string = '*${searchTerm.toLowerCase()}*'`] : []),
      ],
      page: apiPage,
      per_page: perPage,
      ...(sortBy && {
        sort_by: sortBy ? [sortBy] : query.params.sort_by,
        sort_dir: sortDir ? [sortDir] : query.params.sort_dir,
      }),
    },
  );

  const {
    apiPage: apiPageMaximized,
    setTablePage: setTablePageMaximized,
    tablePage: tablePageMaximized,
  } = usePagination({
    tableId: 'table-visualization-card',
  });

  const queryResultMaximized = usePlatformExecuteAdHocQueryTeam(
    currentMembership.team.name,
    { query: query.baseQuery },
    {
      ...query.params,
      filter: [
        ...(query.params.filter ?? []),
        ...(searchTerm ? [`search_string = '*${searchTerm.toLowerCase()}*'`] : []),
      ],
      page: apiPageMaximized,
      per_page: 50,
      ...(sortBy && {
        sort_by: sortBy ? [sortBy] : query.params.sort_by,
        sort_dir: sortDir ? [sortDir] : query.params.sort_dir,
      }),
    },
    {
      query: {
        // ensure it was triggered, and also wait for the base query to finish first
        enabled: hasTriggeredMaximizedFetch && !queryResult.isLoading && !queryResult.isError,
      },
    },
  );

  // reset pagination when filters change
  useEffect(() => {
    setTablePage(0);
    setTablePageMaximized(0);
  }, [searchTerm, query.params.filter, setTablePage, setTablePageMaximized]);

  const handleFieldSort = (field: string) => {
    setSortBy(field);
    setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
  };

  return {
    handleFieldSort,
    maximized,
    perPage: maximized ? 50 : defaultPerPage,
    queryResult,
    queryResultMaximized,
    searchTerm,
    setMaximized,
    setSearchTerm,
    setTablePage: maximized ? setTablePageMaximized : setTablePage,
    sortBy,
    sortDir,
    tablePage: maximized ? tablePageMaximized : tablePage,
    triggerMaximizedFetch: () => setHasTriggeredMaximizedFetch(true),
  };
};
